/* Somber — Testimonials rotator.
   Cycles through sets of review cards (one featured + compact cards),
   crossfading from one set to the next. Pauses while hovered or focused,
   while off screen or in a background tab, and never auto-rotates for
   visitors who prefer reduced motion. */
if (!customElements.get('testimonials-rotator')) {
  customElements.define(
    'testimonials-rotator',
    class TestimonialsRotator extends HTMLElement {
      connectedCallback() {
        this.sets = Array.from(this.querySelectorAll(':scope > .testimonials__set'));
        if (this.sets.length < 2) return;

        this.index = 0;
        this.interval = (Number(this.dataset.interval) || 8) * 1000;
        this.holds = new Set();

        this.hold = (reason, on) => {
          if (on) this.holds.add(reason);
          else this.holds.delete(reason);
          this.schedule();
        };

        this.addEventListener('mouseenter', () => this.hold('hover', true));
        this.addEventListener('mouseleave', () => this.hold('hover', false));
        this.addEventListener('focusin', () => this.hold('focus', true));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.hold('focus', false);
        });

        this.onVisibility = () => this.hold('hidden', document.hidden);
        document.addEventListener('visibilitychange', this.onVisibility);

        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        // Device setting or the accessibility menu's "Pause animations" both hold the rotation.
        const still = () => document.documentElement.classList.contains('a11y-still');
        this.onMotion = () => this.hold('motion', this.motion.matches || still());
        this.motion.addEventListener('change', this.onMotion);
        document.addEventListener('somber:a11y-change', this.onMotion);
        if (this.motion.matches || still()) this.holds.add('motion');

        this.holds.add('offscreen');
        this.observer = new IntersectionObserver((entries) => {
          this.hold('offscreen', !entries[0].isIntersecting);
        });
        this.observer.observe(this);

        // Theme editor: jump to the set holding the selected block and stay there.
        this.addEventListener('shopify:block:select', (event) => {
          const set = event.target.closest('.testimonials__set');
          if (set) this.show(this.sets.indexOf(set));
          this.hold('editor', true);
        });
        this.addEventListener('shopify:block:deselect', () => this.hold('editor', false));
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        if (this.observer) this.observer.disconnect();
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.motion) this.motion.removeEventListener('change', this.onMotion);
        if (this.onMotion) document.removeEventListener('somber:a11y-change', this.onMotion);
      }

      schedule() {
        clearTimeout(this.timer);
        if (this.holds.size) return;
        this.timer = setTimeout(() => this.show(this.index + 1), this.interval);
      }

      show(index) {
        this.index = (index + this.sets.length) % this.sets.length;
        this.sets.forEach((set, i) => {
          const active = i === this.index;
          set.classList.toggle('is-active', active);
          set.inert = !active;
          if (active) set.removeAttribute('aria-hidden');
          else set.setAttribute('aria-hidden', 'true');
        });
        this.schedule();
      }
    }
  );
}
